import { type FC, type PropsWithChildren, useEffect } from 'react';
import { getMapBy } from '../common/utils';
import { useSystemClassifierItemsBy } from '../configuration';
import { createStore } from '../common/Store';
import {
  DataObjectFormDataType,
  type DimensionFormDataType,
  type IndicatorFormDataType,
} from '../dataobject/types';
import { DataObjectGroupExtendedDto } from '../dataObjectGroup';
import { AlgorithmTestCalculationType } from './algorithms/testRun/schema/types';
import { ValidationRuleTestValidationType } from './schema';

export interface dataObjectDescriptionVersionStoreData {
  dataObjectDescriptionVersion: DataObjectFormDataType;
  groupData?: DataObjectGroupExtendedDto;
  openTestCalculate?: (params: AlgorithmTestCalculationType) => void;
  openTestValidate?: (params: ValidationRuleTestValidationType) => void;
  confidentialityStatusCode: string | undefined;
  targetDimensionsAndIndicatorsByCode: Map<
    string,
    DimensionFormDataType | IndicatorFormDataType
  >;
}

export const DataObjectDescriptionVersionStore =
  createStore<dataObjectDescriptionVersionStoreData>();

const { useValue, Provider } = DataObjectDescriptionVersionStore;

export const DataObjectDescriptionVersionStoreProvider: FC<
  PropsWithChildren<{
    initialData: Omit<
      dataObjectDescriptionVersionStoreData,
      'confidentialityStatusCode' | 'targetDimensionsAndIndicatorsByCode'
    >;
  }>
> = (props) => {
  const { initialData, children } = props;
  return (
    <Provider
      initialData={initialData as dataObjectDescriptionVersionStoreData}
    >
      <Computer />
      {children}
    </Provider>
  );
};

const Computer: FC = () => {
  useConfidentialityStatusCodeComputer();
  useTargetDimensionsAndIndicatorsByCodeComputer();
  return null;
};

const useConfidentialityStatusCodeComputer = () => {
  const [{ confidentialityStatusId }] = useValue(
    'dataObjectDescriptionVersion',
  );
  const [, setComputed] = useValue('confidentialityStatusCode');

  const confidentialityStatuses = useSystemClassifierItemsBy(
    ['CONFIDENTIALITY_STATUS'],
    'id',
  ).CONFIDENTIALITY_STATUS;

  useEffect(() => {
    setComputed(
      confidentialityStatusId
        ? confidentialityStatuses.get(confidentialityStatusId)?.code
        : undefined,
    );
  }, [setComputed, confidentialityStatuses, confidentialityStatusId]);
};

const useTargetDimensionsAndIndicatorsByCodeComputer = () => {
  const [{ dimensions, indicators }] = useValue('dataObjectDescriptionVersion');
  const [, setComputed] = useValue('targetDimensionsAndIndicatorsByCode');

  useEffect(() => {
    setComputed(
      new Map<string, DimensionFormDataType | IndicatorFormDataType>([
        ...getMapBy(dimensions || [], 'code'),
        ...getMapBy(indicators || [], 'code'),
      ]),
    );
  }, [setComputed, dimensions, indicators]);
};
